"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getManifestsFromRerunGroupsByType = exports.getManifestsToDelete = exports.getGroupForRerun = exports.getCurrentIssues = exports.getCurrentManifestIds = exports.getCurrentManifestForRerunGroup = exports.getManifestRerunGroupsByType = exports.getManifestRerunGroups = exports.getRunIssues = exports.getRunProgress = exports.getRunDuration = exports.getMetadata = void 0;
/**
 * Get the metadata object from a manifest, defaulting to an empty object if it does not exist.
 */
const getMetadata = (run) => {
    return run.body?.metadata || {};
};
exports.getMetadata = getMetadata;
/**
 * Get the duration of a run in minutes.
 */
const getRunDuration = (run) => {
    const metadata = (0, exports.getMetadata)(run);
    const duration = metadata.duration && metadata.duration > 0
        ? metadata.duration / 1000 / 60
        : 0;
    return duration;
};
exports.getRunDuration = getRunDuration;
/**
 * Get the progress of a run as a percentage of completed steps.
 */
const getRunProgress = (run) => {
    const metadata = (0, exports.getMetadata)(run);
    const steps = metadata.progress?.filter(step => step.text) || [];
    let currentStepIndex = steps.findIndex(step => step.status === 'current');
    if (currentStepIndex === -1) {
        currentStepIndex = 0;
    }
    const totalSteps = steps.length;
    const completedSteps = metadata.complete ? totalSteps : currentStepIndex;
    const progress = Math.trunc((completedSteps / totalSteps) * 100) || 0;
    return progress;
};
exports.getRunProgress = getRunProgress;
/**
 * Get the issues from a run, mapping to the AxeExtensionIssue type.
 */
const getRunIssues = (run) => {
    const metadata = (0, exports.getMetadata)(run);
    const issues = (metadata.issues || []).map(issue => {
        const mappedIssue = {
            ...issue,
            isManual: issue.is_manual
        };
        delete mappedIssue.is_manual;
        return mappedIssue;
    });
    return issues;
};
exports.getRunIssues = getRunIssues;
/**
 * Group manifests into rerun groups, such that each group contains a chain of manifests
 * based on the __rerunFromManifestId field. The first manifest in each group is the
 * latest run of the chain and the last manifest is the earliest run. The groups are
 * sorted by the latest run of the first manifest in each group.
 *
 * The general algorithm is as follows:
 * 1. Separate manifests into original manifests and manifests that are reruns
 * 2. For each original manifest, find all rerun manifests in the "rerun group"
 * 3. Sort rerun groups by the latest run
 */
const getManifestRerunGroups = (manifests) => {
    const originalManifests = [];
    const manifestsByRerunId = {};
    const manifestsById = {};
    // 1. Separate manifests into original manifests and manifests that are reruns
    for (const manifest of manifests) {
        if (manifest.body.__rerunFromManifestId) {
            manifestsByRerunId[manifest.body.__rerunFromManifestId] = manifest;
        }
        else {
            originalManifests.push(manifest);
        }
        manifestsById[manifest.id] = manifest;
    }
    // Handle the edge case where a manifest is a rerun but the original manifest is not in the list.
    // This should not happen, and if it does, it likely means that an original manifest was deleted
    // without clearing the rerunFromManifestId field. In this case, we will treat the rerun manifest
    // as an original manifest.
    Object.keys(manifestsByRerunId).forEach(rerunId => {
        if (!manifestsById[rerunId]) {
            originalManifests.push(manifestsByRerunId[rerunId]);
        }
    });
    // 2. For each original manifest, find all rerun manifests in the "rerun group"
    const rerunGroups = originalManifests.map((originalManifest) => {
        const rerunGroup = [originalManifest];
        let currentManifest = originalManifest;
        while (currentManifest) {
            const rerunManifest = manifestsByRerunId[currentManifest.id];
            if (rerunManifest) {
                // We reverse the rerun group later to get the correct order. This is faster
                // than unshifting the rerun manifest to the rerun group.
                rerunGroup.push(rerunManifest);
            }
            currentManifest = rerunManifest;
        }
        // Reverse the rerun group to get the correct order
        return rerunGroup.reverse();
    });
    // 3. Sort rerun groups by the latest run
    const sortedRerunGroups = rerunGroups.sort((groupA, groupB) => {
        const groupALatestRun = groupA[0];
        const groupBLatestRun = groupB[0];
        return new Date(groupALatestRun.updated_at || groupALatestRun.created_at || 0) < new Date(groupBLatestRun.updated_at || groupBLatestRun.created_at || 0)
            ? 1
            : -1;
    });
    return sortedRerunGroups;
};
exports.getManifestRerunGroups = getManifestRerunGroups;
/**
 * For each guide type, group manifests into rerun groups.
 */
const getManifestRerunGroupsByType = (manifestsByType) => {
    const manifestRerunGroups = Object.keys(manifestsByType).reduce((acc, type) => {
        acc[type] = (0, exports.getManifestRerunGroups)(manifestsByType[type]);
        return acc;
    }, {});
    return manifestRerunGroups;
};
exports.getManifestRerunGroupsByType = getManifestRerunGroupsByType;
/**
 * Get the "current" manifest for a rerun group. The current manifest is the one that is either the
 * most recently completed manifest or the first manifest in the rerun group (should only be one
 * and it should be "in progress").
 */
const getCurrentManifestForRerunGroup = (manifestRerunGroup) => {
    const mostRecentCompletedManifest = manifestRerunGroup.find(manifest => !!(0, exports.getMetadata)(manifest).complete);
    return mostRecentCompletedManifest || manifestRerunGroup[0];
};
exports.getCurrentManifestForRerunGroup = getCurrentManifestForRerunGroup;
/**
 * Get the IDs of the current manifests for all rerun groups.
 */
const getCurrentManifestIds = (manifestRerunGroups) => {
    return manifestRerunGroups.reduce((acc, rerunGroup) => {
        const currentManifest = (0, exports.getCurrentManifestForRerunGroup)(rerunGroup);
        acc.add(currentManifest.id);
        return acc;
    }, new Set());
};
exports.getCurrentManifestIds = getCurrentManifestIds;
/**
 * Filter out issues that are not associated with the current manifests.
 */
const getCurrentIssues = (issues, manifestRerunGroups) => {
    const currentManifestIds = (0, exports.getCurrentManifestIds)(manifestRerunGroups);
    return issues.filter(issue => !issue.manifestId || currentManifestIds.has(issue.manifestId));
};
exports.getCurrentIssues = getCurrentIssues;
/**
 * Gets a run group for a given manifest.
 */
const getGroupForRerun = (rerun, rerunGroups) => {
    return (rerunGroups.find(group => group.some(manifest => manifest.id === rerun.id)) || null);
};
exports.getGroupForRerun = getGroupForRerun;
/**
 * Returns a list of manifests to delete based on the given manifest and rerun groups.
 * When a rerun is deleted, it and all following reruns in the group should be deleted.
 */
const getManifestsToDelete = (rerun, rerunGroups) => {
    const rerunGroup = (0, exports.getGroupForRerun)(rerun, rerunGroups);
    if (!rerunGroup) {
        return [];
    }
    const rerunIndex = rerunGroup.findIndex(run => run.id === rerun.id);
    return rerunIndex !== -1 ? rerunGroup.slice(0, rerunIndex + 1) : [];
};
exports.getManifestsToDelete = getManifestsToDelete;
/**
 * The input for this is the output of 'getManifestRerunGroupsByType' method which is above.
 * The output is a map of guide types to manifests.
 * @param manifestRerunGroupsByType
 */
const getManifestsFromRerunGroupsByType = (manifestRerunGroupsByType) => {
    return Object.entries(manifestRerunGroupsByType).reduce((acc, current) => {
        const [type, rerunGroups] = current;
        acc[type] = rerunGroups.map(rerunGroup => (0, exports.getCurrentManifestForRerunGroup)(rerunGroup));
        return acc;
    }, {});
};
exports.getManifestsFromRerunGroupsByType = getManifestsFromRerunGroupsByType;
//# sourceMappingURL=manifest-utils.js.map